import { openDB } from '@utility/indexedDB_utils';

export const importMergeData = async (jsonData) => {
  try {
    const db = await openDB();
    const idMappings = {
      exercises: {},      // exercise_id mapping
      muscles: {},        // muscle_id mapping
      muscle_groups: {},  // muscle_groups_id mapping - ADDED THIS LINE
      sessions: {},       // session_id mapping
      workoutLogs: {},    // workout_log_id mapping
      templates: {}       // template_id mapping
    };

    // Step 1: Process reference tables (exercises, muscles) without duplicates
    await processReferenceTables(db, jsonData, idMappings);
    
    // Step 2: Process workout sessions and all related data as complete units
    await processWorkoutSessions(db, jsonData, idMappings);
    
    // Step 3: Process templates and related data
    await processTemplates(db, jsonData, idMappings);
    
    return true;
  } catch (error) {
    console.error("Merge failed:", error);
    throw error;
  }
};

const processReferenceTables = async (db, jsonData, idMappings) => {
  const referenceTables = ['exercises', 'muscles', 'muscle_groups'];
  
  for (const tableName of referenceTables) {
    const table = jsonData.tables.find(t => t.name === tableName);
    if (!table) continue;
    
    const tx = db.transaction(tableName, 'readwrite');
    const store = tx.objectStore(tableName);
    
    for (const row of table.rows) {
      const item = table.columns.reduce((obj, col, index) => {
        obj[col] = row[index];
        return obj;
      }, {});
      
      // For reference tables, we match by name to avoid duplicates
      const existingId = await findExistingByUniqueField(store, item);
      
      if (existingId) {
        idMappings[tableName][item.id] = existingId;
      } else {
        const newId = await addItem(store, item);
        idMappings[tableName][item.id] = newId;
      }
    }
    
    await transactionComplete(tx);
  }
};

const processWorkoutSessions = async (db, jsonData, idMappings) => {
  const sessionsTable = jsonData.tables.find(t => t.name === 'workout_sessions');
  if (!sessionsTable) return;

  // Process sessions in chronological order
  const sessions = sessionsTable.rows.map(row => 
    sessionsTable.columns.reduce((obj, col, index) => {
      obj[col] = row[index];
      return obj;
    }, {})
  ).sort((a, b) => new Date(a.session_date) - new Date(b.session_date));

  for (const session of sessions) {
    // Add the session (will get new auto-incremented ID)
    const newSessionId = await addItem(
      db.transaction('workout_sessions', 'readwrite')
        .objectStore('workout_sessions'), 
      {...session}
    );
    
    idMappings.sessions[session.id] = newSessionId;
    
    // Process all logs for this session
    const logsTable = jsonData.tables.find(t => t.name === 'workout_log');
    if (logsTable) {
      const sessionLogs = logsTable.rows
        .map(row => logsTable.columns.reduce((obj, col, index) => {
          obj[col] = row[index];
          return obj;
        }, {}))
        .filter(log => log.session_id === session.id);
      
      for (const log of sessionLogs) {
        // Add workout log with remapped session_id and exercise_id
        const newLogId = await addItem(
          db.transaction('workout_log', 'readwrite')
            .objectStore('workout_log'),
          {
            ...log,
            id: undefined,
            session_id: newSessionId,
            exercise_id: idMappings.exercises[log.exercise_id] || log.exercise_id
          }
        );
        
        idMappings.workoutLogs[log.id] = newLogId;
        
        // Process all sets for this log
        const setsTable = jsonData.tables.find(t => t.name === 'sets');
        if (setsTable) {
          const logSets = setsTable.rows
            .map(row => setsTable.columns.reduce((obj, col, index) => {
              obj[col] = row[index];
              return obj;
            }, {}))
            .filter(set => set.workout_log_id === log.id);
          
          for (const set of logSets) {
            await addItem(
              db.transaction('sets', 'readwrite')
                .objectStore('sets'),
              {
                ...set,
                id: undefined,
                workout_log_id: newLogId
              }
            );
          }
        }
      }
    }
  }
};

const processTemplates = async (db, jsonData, idMappings) => {
  const templatesTable = jsonData.tables.find(t => t.name === 'workout_templates');
  if (!templatesTable) return;

  for (const row of templatesTable.rows) {
    const template = templatesTable.columns.reduce((obj, col, index) => {
      obj[col] = row[index];
      return obj;
    }, {});

    // Check if template with same name exists
    const existingTemplateId = await findExistingByUniqueField(
      db.transaction('workout_templates', 'readonly')
        .objectStore('workout_templates'),
      template
    );

    if (!existingTemplateId) {
      const newTemplateId = await addItem(
        db.transaction('workout_templates', 'readwrite')
          .objectStore('workout_templates'),
        {...template, id: undefined}
      );
      
      idMappings.templates[template.id] = newTemplateId;

      // Process template exercises
      const templateExercisesTable = jsonData.tables.find(t => t.name === 'workout_template_exercises');
      if (templateExercisesTable) {
        const templateExercises = templateExercisesTable.rows
          .map(row => templateExercisesTable.columns.reduce((obj, col, index) => {
            obj[col] = row[index];
            return obj;
          }, {}))
          .filter(ex => ex.template_id === template.id);
        
        for (const ex of templateExercises) {
          await addItem(
            db.transaction('workout_template_exercises', 'readwrite')
              .objectStore('workout_template_exercises'),
            {
              ...ex,
              id: undefined,
              template_id: newTemplateId,
              exercise_id: idMappings.exercises[ex.exercise_id] || ex.exercise_id
            }
          );
        }
      }
    }
  }
};

// Helper function to find existing items by unique fields
const findExistingByUniqueField = (store, item) => {
  return new Promise((resolve) => {
    let indexName, key;
    
    if (store.name === 'exercises') {
      indexName = 'name';
      key = item.name;
    } else if (store.name === 'muscles' || store.name === 'muscle_groups') {
      indexName = 'name';
      key = item.name;
    } else if (store.name === 'workout_templates') {
      indexName = 'name';
      key = item.name;
    } else {
      resolve(null);
      return;
    }
    
    const request = store.index(indexName).get(key);
    request.onsuccess = () => resolve(request.result?.id || null);
    request.onerror = () => resolve(null);
  });
};

// Helper functions
const addItem = (store, item) => {
  return new Promise((resolve, reject) => {
    // Clone the item to avoid modifying the original
    const itemToAdd = {...item};
    
    // If using auto-increment, ensure no id is provided
    if (store.keyPath && store.autoIncrement && 'id' in itemToAdd) {
      delete itemToAdd.id;
    }
    
    const request = store.add(itemToAdd);
    request.onsuccess = () => resolve(request.result);
    request.onerror = (event) => {
      console.error('Failed to add item:', itemToAdd, 'Error:', event.target.error);
      reject(event.target.error);
    };
  });
};

const transactionComplete = (tx) => {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = (event) => reject(event.target.error);
  });
};